import CloseIcon from "@mui/icons-material/Close";
import ReplyIcon from "@mui/icons-material/Reply";
import SendIcon from "@mui/icons-material/Send";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import type { MessageDocument } from "../../../api/documents/message-documents";
import { addEmailToQueue } from "../../../api/messaging/email";
import { addLinkedinMessageToQueue } from "../../../api/messaging/linkedin-message";
import type { PersonEmailMessage } from "../../../api/person/emails";
import type { PersonEmail } from "../../../api/personemail/person-email";
import { getEmailSignature } from "../../../api/user";
import { formatDateTime } from "../../../utils/date-formatting";
import { normalizeReplySubject } from "../../../utils/email-subject";
import { MessageDocumentsField } from "../../messaging/message-documents-field";
import { EmojiPickerButton } from "../../shared/EmojiPickerButton";

export type EmailReplyContext = {
	type: "email";
	emailJournalId: number;
	originalSubject: string;
	originalFrom: string;
	originalDate: string;
	originalText: string;
};

export type LinkedInReplyContext = {
	type: "linkedin";
	originalFrom: string;
	originalDate: string;
	originalText: string;
};

export type ReplyContext = EmailReplyContext | LinkedInReplyContext;

interface ReplyDialogProps {
	open: boolean;
	onClose: () => void;
	replyTo: ReplyContext | null;
	personId: number;
	userId: number;
	personEmails: PersonEmail[];
	onSuccess?: () => void;
}

/**
 * Formats the original message text with ">" prefix for quoting
 */
function formatQuotedText(text: string): string {
	return text
		.split("\n")
		.map((line) => `> ${line}`)
		.join("\n");
}

/**
 * Builds the initial quoted reply text that pre-populates the text field.
 * If a signature is provided, it will be placed between the reply area and quoted text.
 */
function buildInitialReplyText(
	originalDate: string,
	originalFrom: string,
	originalText: string,
	signature?: string | null
): string {
	const formattedDate = formatDateTime(originalDate);
	const quotedText = formatQuotedText(originalText);

	// Signature goes between reply area and quoted text
	const signatureBlock = signature ? `\n\n${signature}\n` : "\n";

	return `${signatureBlock}
On ${formattedDate}, ${originalFrom} wrote:
${quotedText}`;
}

/**
 * Checks if user has added actual reply content above the signature/quote.
 * Returns true if there's meaningful content at the start of the message.
 * We check for content before both signature markers and quote markers.
 */
function hasReplyContent(replyText: string, signature?: string | null): boolean {
	// Find where the "automatic" content starts (signature or quote)
	// The user's reply should be at the very beginning
	const quoteMarkerIndex = replyText.indexOf("\nOn ");
	const signatureIndex = signature ? replyText.indexOf(signature) : -1;

	// Find the earliest marker
	let markerIndex = -1;
	if (signatureIndex !== -1 && quoteMarkerIndex !== -1) {
		markerIndex = Math.min(signatureIndex, quoteMarkerIndex);
	} else if (signatureIndex !== -1) {
		markerIndex = signatureIndex;
	} else if (quoteMarkerIndex !== -1) {
		markerIndex = quoteMarkerIndex;
	}

	if (markerIndex === -1) {
		// No markers found - check if there's any content
		return replyText.trim().length > 0;
	}

	// Check if there's content before the marker
	const contentBeforeMarker = replyText.substring(0, markerIndex).trim();
	return contentBeforeMarker.length > 0;
}

export const ReplyDialog = ({
	open,
	onClose,
	replyTo,
	personId,
	userId,
	personEmails,
	onSuccess,
}: ReplyDialogProps) => {
	const [replyText, setReplyText] = useState("");
	const [subject, setSubject] = useState("");
	const [selectedEmailId, setSelectedEmailId] = useState<number | "new" | "">("");
	const [sending, setSending] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [signature, setSignature] = useState<string | null>(null);
	const [signatureLoaded, setSignatureLoaded] = useState(false);
	const [confirmClose, setConfirmClose] = useState(false);
	// A reply attaches documents exactly like a fresh message does.
	const [documents, setDocuments] = useState<MessageDocument[]>([]);

	// Cursor position for emoji insertion
	const cursorRef = useRef({ start: 0, end: 0 });
	const textInputRef = useRef<HTMLTextAreaElement>(null);

	// Track the replyTo context we've initialized for (prevents re-init on unrelated re-renders)
	const initializedForRef = useRef<number | null>(null);

	// Fetch user's email signature when dialog opens
	useEffect(() => {
		if (!open) return;
		getEmailSignature()
			.then((res) => {
				if (res.signature_text) {
					setSignature(res.signature_text);
				}
			})
			.finally(() => {
				setSignatureLoaded(true);
			});
	}, [open]);

	// Pre-populate fields when dialog opens - wait for signature to load first
	useEffect(() => {
		if (!open || !replyTo || !signatureLoaded) {
			return;
		}

		// Use emailJournalId for email, or a hash for LinkedIn to track initialization
		const contextId =
			replyTo.type === "email"
				? replyTo.emailJournalId
				: replyTo.originalDate.length;

		// Only initialize once per context
		if (initializedForRef.current === contextId) {
			return;
		}
		initializedForRef.current = contextId;

		if (replyTo.type === "email") {
			// Gmail/Outlook style: reply at top, signature, then quoted text below
			const quotedText = buildInitialReplyText(
				replyTo.originalDate,
				replyTo.originalFrom,
				replyTo.originalText,
				signature
			);
			setReplyText(quotedText);
			setSubject(normalizeReplySubject(replyTo.originalSubject));

			// Pre-select the PersonEmail matching the sender's address
			const match = personEmails.find((pe) => pe.email === replyTo.originalFrom);
			setSelectedEmailId(match?.id ?? "new");
		} else {
			setReplyText("");
			setSubject("");
		}
	}, [open, replyTo, signatureLoaded, signature]);

	const handleCloseConfirmed = () => {
		setReplyText("");
		setSubject("");
		setSelectedEmailId("");
		setError(null);
		setConfirmClose(false);
		initializedForRef.current = null; // Reset so next open re-initializes
		onClose();
	};

	const handleCloseRequest = () => {
		const hasContent =
			replyTo?.type === "email"
				? hasReplyContent(replyText, signature)
				: replyText.trim().length > 0;

		if (hasContent) {
			setConfirmClose(true);
		} else {
			handleCloseConfirmed();
		}
	};

	const handleSend = async () => {
		if (!replyTo || !replyText.trim()) {
			setError("Reply text is required");
			return;
		}

		if (replyTo.type === "email") {
			if (!subject.trim()) {
				setError("Subject is required");
				return;
			}
			if (!hasReplyContent(replyText, signature)) {
				setError("Please add your reply above the quoted text");
				return;
			}
		}

		setSending(true);
		setError(null);

		try {
			if (replyTo.type === "email") {
				await addEmailToQueue({
					from: userId,
					to: personId,
					text: replyText,
					sendImmediately: true,
					subject: subject.trim(),
					inReplyTo: replyTo.emailJournalId,
					personEmailId:
						typeof selectedEmailId === "number"
							? selectedEmailId
							: undefined,
					documentIds: documents.map((doc) => doc.id),
				});
			} else {
				// LinkedIn - just send the reply text, no quoting needed
				await addLinkedinMessageToQueue({
					from: userId,
					to: personId,
					text: replyText,
					sendImmediately: true,
					documentIds: documents.map((doc) => doc.id),
				});
			}

			onSuccess?.();
			handleCloseConfirmed();
		} catch (e) {
			setError(e instanceof Error ? e.message : "Failed to send reply");
		} finally {
			setSending(false);
		}
	};

	const handleEmojiSelect = (emoji: string) => {
		const start = cursorRef.current.start;
		const end = cursorRef.current.end;
		const newText =
			replyText.substring(0, start) + emoji + replyText.substring(end);
		setReplyText(newText);

		const newCursorPos = start + emoji.length;
		cursorRef.current = { start: newCursorPos, end: newCursorPos };

		setTimeout(() => {
			if (textInputRef.current) {
				textInputRef.current.focus();
				textInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
			}
		}, 0);
	};

	const handleTextSelect = () => {
		if (textInputRef.current) {
			cursorRef.current = {
				start: textInputRef.current.selectionStart ?? 0,
				end: textInputRef.current.selectionEnd ?? 0,
			};
		}
	};

	if (!replyTo) return null;

	const isEmail = replyTo.type === "email";
	const title = isEmail ? "Reply to Email" : "Reply on LinkedIn";

	return (
		<Dialog
			open={open}
			onClose={(_event, reason) => {
				if (reason === "escapeKeyDown" || reason === "backdropClick") {
					handleCloseRequest();
				}
			}}
			maxWidth="md"
			fullWidth
			data-component="reply-dialog"
		>
			<DialogTitle data-component="reply-dialog-title">
				<Stack
					direction="row"
					alignItems="center"
					spacing={1}
					data-component="reply-title-row"
				>
					<ReplyIcon data-component="reply-icon" />
					<Typography
						variant="h6"
						sx={{ flex: 1 }}
						data-component="reply-title-text"
					>
						{title}
					</Typography>
					<IconButton
						onClick={handleCloseRequest}
						size="small"
						data-component="close-reply-button"
					>
						<CloseIcon />
					</IconButton>
				</Stack>
			</DialogTitle>
			<DialogContent data-component="reply-dialog-content">
				<Stack spacing={2} sx={{ mt: 1 }} data-component="reply-form-stack">
					{/* Recipient select for emails */}
					{isEmail && (
						<FormControl fullWidth data-component="reply-recipient-control">
							<InputLabel>To</InputLabel>
							<Select
								value={selectedEmailId}
								label="To"
								onChange={(e) =>
									setSelectedEmailId(e.target.value as number | "new")
								}
								disabled={sending}
								data-component="reply-recipient-select"
							>
								{personEmails.map((email) => (
									<MenuItem key={email.id} value={email.id}>
										{email.email}
									</MenuItem>
								))}
								{replyTo.type === "email" &&
									!personEmails.some(
										(pe) => pe.email === replyTo.originalFrom
									) && (
										<MenuItem value="new">
											{replyTo.originalFrom} (new)
										</MenuItem>
									)}
							</Select>
						</FormControl>
					)}
					{/* Editable subject field for emails */}
					{isEmail && (
						<TextField
							label="Subject"
							fullWidth
							value={subject}
							onChange={(e) => setSubject(e.target.value)}
							disabled={sending}
							data-component="reply-subject-field"
						/>
					)}

					{/* Reply text input - Gmail/Outlook style */}
					<Box data-component="reply-text-wrapper">
						<TextField
							label="Message"
							multiline
							rows={12}
							fullWidth
							value={replyText}
							onChange={(e) => setReplyText(e.target.value)}
							onSelect={handleTextSelect}
							inputRef={textInputRef}
							placeholder="Type your reply here..."
							disabled={sending}
							autoFocus
							data-component="reply-text-field"
							helperText={
								isEmail
									? "Your reply goes at the top. Original message quoted below."
									: undefined
							}
						/>
						<Box
							sx={{
								display: "flex",
								justifyContent: "flex-end",
								mt: 0.5,
							}}
							data-component="emoji-picker-wrapper"
						>
							<EmojiPickerButton
								onEmojiSelect={handleEmojiSelect}
								disabled={sending}
							/>
						</Box>
					</Box>

					<MessageDocumentsField
						value={documents}
						onChange={setDocuments}
						disabled={sending}
					/>

					{/* Error display */}
					{error && (
						<Alert severity="error" data-component="reply-error">
							{error}
						</Alert>
					)}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }} data-component="reply-dialog-actions">
				<Button
					onClick={handleSend}
					variant="contained"
					disabled={
						sending ||
						!replyText.trim() ||
						(isEmail &&
							(!subject.trim() ||
								selectedEmailId === "" ||
								!hasReplyContent(replyText, signature)))
					}
					startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
					data-component="send-reply-button"
				>
					Send
				</Button>
			</DialogActions>
			<Dialog
				open={confirmClose}
				onClose={() => setConfirmClose(false)}
				data-component="reply-confirm-close-dialog"
			>
				<DialogTitle data-component="confirm-close-title">
					Discard reply?
				</DialogTitle>
				<DialogContent data-component="confirm-close-content">
					<Typography data-component="confirm-close-text">
						Your reply has not been sent. Are you sure you want to close?
					</Typography>
				</DialogContent>
				<DialogActions data-component="confirm-close-actions">
					<Button
						onClick={() => setConfirmClose(false)}
						data-component="keep-editing-button"
					>
						Keep editing
					</Button>
					<Button
						onClick={handleCloseConfirmed}
						color="error"
						data-component="discard-reply-button"
					>
						Discard
					</Button>
				</DialogActions>
			</Dialog>
		</Dialog>
	);
};

/**
 * Helper to create EmailReplyContext from a PersonEmailMessage
 */
export function createEmailReplyContext(email: PersonEmailMessage): EmailReplyContext {
	return {
		type: "email",
		emailJournalId: email.email_journal_id,
		originalSubject: email.subject,
		originalFrom: email.email_from,
		originalDate: email.date,
		originalText: email.text_display || email.text,
	};
}

/**
 * Helper to create LinkedInReplyContext from conversation data
 */
export function createLinkedInReplyContext(
	from: string,
	date: string,
	text: string
): LinkedInReplyContext {
	return {
		type: "linkedin",
		originalFrom: from,
		originalDate: date,
		originalText: text,
	};
}
